import {
  AttributeOptions,
  MultiSelectOption,
  TaskFormComponentProps,
  TaskTemplateForm,
} from '@dispatch/shared/types';
import { CheckBoxField, SelectField } from '@dispatch/web/ui';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import { Card, CardContent, Grid, Stack } from '@mui/material';
import React, { useCallback } from 'react';
import {
  Control,
  FieldErrors,
  FieldValues,
  useFieldArray,
  UseFormSetValue,
} from 'react-hook-form';

enum ATTRIBUTE_TYPES {
  MULTI = 'multi-select',
  BOOL = 'bool',
  TEXT = 'text',
}

export const AttributesField = ({
  attributeDefinitions,
  control,
  errors,
  name,
  setValue,
}: TaskFormComponentProps) => {
  const { fields, append, remove } = useFieldArray({
    control,
    name,
  });

  const appendAttribute = () => {
    return append({
      id: '',
      name: '',
      value: '',
      cat: 'none',
      nonCat: 'none',
      useProficiency: true,
      minimumProficiency: '0',
      isPrepopulated: true,
    });
  };

  const minProficiencies = () => {
    const profs = [];
    for (let i = 0; i <= 10; i++) {
      profs.push({ value: i, title: `${i}` });
    }

    return profs;
  };

  const findSelectedAttribute = useCallback(
    (item: { id: string; name?: string }) => {
      return attributeDefinitions?.filter(
        (ad: AttributeOptions) => ad.value === item.name
      );
    },
    [attributeDefinitions]
  );

  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', width: '100%' }}>
      {fields.length ? (
        <div style={{ width: '100%' }}>
          {fields.map((item: { id: string; attribute?: string }, index) => {
            const selectedAttribute = findSelectedAttribute(item);

            return (
              <Grid
                data-testid="attribute-field"
                container
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                }}
                key={item.id}
              >
                <Grid item xs={11}>
                  <Card
                    sx={{ width: '100%', marginBottom: '0.625rem' }}
                    key={index}
                  >
                    <CardContent>
                      <Stack>
                        <Grid container spacing={2} alignItems="baseline">
                          <DynamicAttributeFields
                            setValue={setValue}
                            selectedValue={selectedAttribute?.[0]}
                            control={control}
                            errors={errors}
                            attributeDefinitions={attributeDefinitions}
                            index={index}
                          />
                        </Grid>
                        <Grid container spacing={2} marginTop={1}>
                          <Grid item xs={4}>
                            <SelectField
                              id={`attributes[${index}].minimumProficiency`}
                              name={`attributes[${index}].minimumProficiency`}
                              label="Minimum Proficiency"
                              control={control}
                              errors={errors}
                              error={
                                errors['attributes']
                                  ? errors['attributes'][index]
                                      ?.minimumProficiency?.message
                                  : null
                              }
                              options={[...minProficiencies()]}
                            />
                          </Grid>
                          <Grid item sx={{ paddingLeft: '16px !important' }}>
                            <Stack direction="row">
                              <CheckBoxField
                                control={control}
                                id={`attributes[${index}].isPrepopulated`}
                                name={`attributes[${index}].isPrepopulated`}
                                label="Prepopulated on all tasks"
                              />
                              <CheckBoxField
                                control={control}
                                id={`attributes[${index}].useProficiency`}
                                name={`attributes[${index}].useProficiency`}
                                label="Use Proficiency"
                              />
                            </Stack>
                          </Grid>
                        </Grid>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={1}>
                  <div style={{ margin: '0 1.5625rem' }}>
                    <CloseIcon
                      data-testid="remove-attribute"
                      style={{ cursor: 'pointer', color: '#E31B0C' }}
                      onClick={() => remove(index)}
                    />
                  </div>
                  {index === fields.length - 1 ? (
                    <div style={{ margin: '0 1.5625rem' }}>
                      <AddCircleOutlineIcon
                        data-testid="add-attribute"
                        style={{ cursor: 'pointer', color: '#3F51B5' }}
                        onClick={() => appendAttribute()}
                      />
                    </div>
                  ) : null}
                </Grid>
              </Grid>
            );
          })}
        </div>
      ) : (
        <div style={{ width: '100%' }}>
          <Card sx={{ width: '100%' }}>
            <CardContent>
              <div
                style={{
                  display: 'flex',
                  height: '6rem',
                  width: '100%',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <AddCircleOutlineIcon
                    data-testid="add-first-attribute"
                    style={{ cursor: 'pointer' }}
                    onClick={() => appendAttribute()}
                  />
                  <p style={{ margin: '0 1.25rem', fontSize: '1rem' }}>
                    Add an Attribute
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export const DynamicAttributeFields = ({
  control,
  index,
  errors,
  attributeDefinitions,
  selectedValue,
  setValue,
}: Omit<TaskFormComponentProps, 'name'> & {
  index: number;
  selectedValue: any;
}) => {
  const [attributeTypeFieldMeta, setAttributeTypeFieldMeta] = React.useState<{
    type: ATTRIBUTE_TYPES;
    options: AttributeOptions[];
  }>({ type: ATTRIBUTE_TYPES.TEXT, options: [] });
  // handle field width changes for different field types
  const fieldWidth = (type: ATTRIBUTE_TYPES) => {
    if (type === ATTRIBUTE_TYPES.BOOL) {
      return [4, 3, 3];
    } else if (type === ATTRIBUTE_TYPES.MULTI) {
      return [3, 3, 3];
    }

    return [4, 4, 4];
  };

  React.useEffect(() => {
    if (selectedValue) {
      setAttributeTypeFieldMeta({
        type: selectedValue?.valueType as ATTRIBUTE_TYPES,
        options: selectedValue?.attributeDefinitionValues ?? [],
      });
    }
  }, [selectedValue]);

  return (
    <>
      <Grid item xs={fieldWidth(attributeTypeFieldMeta.type)[0]}>
        <SelectField
          id={`attributes[${index}].name`}
          name={`attributes[${index}].name`}
          onValueChange={(attribute) => {
            const option = attribute as MultiSelectOption & {
              valueType: ATTRIBUTE_TYPES;
              attributeDefinitionValues: AttributeOptions[];
              definition: {
                integrationGUID: string;
              };
            };

            setAttributeTypeFieldMeta({
              type: option?.valueType ?? ATTRIBUTE_TYPES.TEXT,
              options: option?.attributeDefinitionValues ?? [],
            });
          }}
          label="Attribute"
          control={control}
          errors={errors}
          error={
            errors['attributes']
              ? errors['attributes'][index]?.name?.message
              : null
          }
          options={attributeDefinitions || []}
        />
      </Grid>

      <AttributeTypeField
        setValue={setValue}
        type={attributeTypeFieldMeta.type}
        options={attributeTypeFieldMeta.options}
        control={control}
        errors={errors}
        index={index}
      />

      <Grid item xs={fieldWidth(attributeTypeFieldMeta.type)[1]}>
        <SelectField
          id={`attributes[${index}].cat`}
          name={`attributes[${index}].cat`}
          label="CAT"
          control={control}
          errors={errors}
          disableClearable
          defaultValue={'none'}
          error={
            errors['attributes']
              ? errors['attributes'][index]?.cat?.message
              : null
          }
          options={[
            { title: 'None', value: 'none' },
            { title: 'Preferred', value: 'preferred' },
            { title: 'Required', value: 'required' },
          ]}
        />
      </Grid>
      <Grid item xs={fieldWidth(attributeTypeFieldMeta.type)[2]}>
        <SelectField
          id={`attributes[${index}].nonCat`}
          name={`attributes[${index}].nonCat`}
          label="Non CAT"
          control={control}
          errors={errors}
          disableClearable
          defaultValue={'none'}
          error={
            errors['attributes']
              ? errors['attributes'][index]?.nonCat?.message
              : null
          }
          options={[
            { title: 'None', value: 'none' },
            { title: 'Preferred', value: 'preferred' },
            { title: 'Required', value: 'required' },
          ]}
        />
      </Grid>
    </>
  );
};

export const AttributeTypeField = ({
  type,
  options = [],
  control,
  index,
  errors,
  setValue,
}: {
  type: ATTRIBUTE_TYPES;
  options: AttributeOptions[];
  control: Control<FieldValues>;
  index: number;
  errors: FieldErrors;
  setValue: UseFormSetValue<TaskTemplateForm & FieldValues> | undefined;
}) => {
  if (type === ATTRIBUTE_TYPES.MULTI) {
    return (
      <Grid item xs={3}>
        <SelectField
          id={`attributes[${index}].value`}
          name={`attributes[${index}].value`}
          label="Value"
          onValueChange={(value) => {
            const option = value as {
              definitionValue?: { integrationGUID: string };
            };

            if (option?.definitionValue?.integrationGUID) {
              // set the attribute definition id as the id for the attribute on the task template
              setValue?.(
                `attributes[${index}].id`,
                option?.definitionValue?.integrationGUID
              );
            }
          }}
          control={control}
          errors={errors}
          error={
            errors['attributes']
              ? errors['attributes'][index]?.value?.message
              : null
          }
          options={[...options]}
        />
      </Grid>
    );
  }

  if (type === ATTRIBUTE_TYPES.BOOL) {
    return (
      <Grid item xs={1}>
        <CheckBoxField
          sx={{
            label: {
              '.MuiFormControlLabel-label': {
                fontSize: '12px !important',
                color: 'rgba(0, 0, 0, 0.6);',
              },
              '&.MuiFormControlLabel-root': {
                marginBottom: '8px',
              },
            },
          }}
          labelPlacement="top"
          control={control}
          id={`attributes[${index}].value`}
          name={`attributes[${index}].value`}
          label="Value"
        />
      </Grid>
    );
  }

  return <></>;
};
